import { useQuery } from "@tanstack/react-query";
import { api, type ContentPurchaseLinkRow } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InfoTip } from "@/components/ui/tooltip";
import type { MetricTipId } from "@/lib/metricsGlossary";
import { formatPLN } from "@/lib/utils";

const SINCE = "2026-08-20";

function pct(share?: number): string {
  if (share == null || Number.isNaN(share)) return "0%";
  return `${Math.round(share * 1000) / 10}%`;
}

function formatWhen(iso?: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pl-PL", {
      timeZone: "Europe/Warsaw",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ContentLinks({ urls }: { urls?: Record<string, string> }) {
  if (!urls) return null;
  const entries = Object.entries(urls).filter(([, v]) => v && /^https?:\/\//i.test(v));
  if (!entries.length) return null;
  return (
    <span className="ml-2 inline-flex flex-wrap gap-2 text-xs">
      {entries.map(([k, href]) => (
        <a key={k} href={href} target="_blank" rel="noreferrer" className="text-brand hover:underline">
          {k}
        </a>
      ))}
    </span>
  );
}

function TierList({
  title,
  tipId,
  hint,
  rows,
  empty,
}: {
  title: string;
  tipId: MetricTipId;
  hint: string;
  rows: ContentPurchaseLinkRow[];
  empty: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {title}
          <InfoTip tipId={tipId} />
        </CardTitle>
        <p className="text-sm text-muted-foreground">{hint}</p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          <ul className="grid gap-3">
            {rows.map((row, i) => {
              const content = row.content;
              return (
                <li
                  key={`${row.tier}-${row.order_ref || i}-${i}`}
                  className="rounded-md border border-border/60 p-3 text-sm"
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-medium tabular-nums">
                      {formatPLN(Number(row.revenue || 0))}
                    </span>
                    <span className="text-muted-foreground">{formatWhen(row.ordered_at)}</span>
                    {row.via ? (
                      <Badge variant="outline" className="font-normal">
                        via={row.via}
                      </Badge>
                    ) : null}
                    {row.product_name ? (
                      <span className="text-muted-foreground">{row.product_name}</span>
                    ) : null}
                  </div>
                  {content ? (
                    <div className="mt-2">
                      <div className="font-medium">{content.title || "treść"}</div>
                      {content.snippet && content.snippet !== content.title ? (
                        <p className="mt-0.5 text-muted-foreground">{content.snippet}</p>
                      ) : null}
                      <div className="mt-1 text-xs text-muted-foreground">
                        {content.kind || ""}
                        {content.hours_from_order != null
                          ? ` · Δ ${content.hours_from_order} h`
                          : ""}
                        {content.triggers ? ` · trigery ${content.triggers}` : ""}
                        <ContentLinks urls={content.urls} />
                      </div>
                    </div>
                  ) : null}
                  {row.note_pl ? (
                    <p className="mt-2 text-xs text-muted-foreground">{row.note_pl}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default function ContentPurchaseLinksPanel() {
  const q = useQuery({
    queryKey: ["content-purchase-links", SINCE],
    queryFn: () => api.siteAnalyticsContentPurchaseLinks({ since: SINCE }),
  });
  const data = q.data;
  const summary = data?.summary;

  return (
    <div className="grid gap-5">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Treść ↔ zakupy</CardTitle>
          <p className="text-sm text-muted-foreground">
            Od {SINCE}. Twarde linki tylko przez ?via=. Reszta to korelacja albo brak pomiaru.
          </p>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm">
          {q.isLoading ? (
            <p className="text-muted-foreground">Ładowanie powiązań…</p>
          ) : q.isError ? (
            <p className="text-destructive">Nie udało się wczytać powiązań.</p>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-4">
                <div className="rounded-md border border-border/60 p-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">Zamówienia</div>
                  <div className="mt-1 text-lg font-medium tabular-nums">{summary?.orders ?? 0}</div>
                  <div className="text-muted-foreground">{formatPLN(Number(summary?.revenue || 0))}</div>
                </div>
                <div className="rounded-md border border-border/60 p-3">
                  <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
                    Powiązane
                    <InfoTip tipId="content_purchase_hard" />
                  </div>
                  <div className="mt-1 text-lg font-medium tabular-nums">{summary?.hard ?? 0}</div>
                  <div className="text-muted-foreground">
                    {formatPLN(Number(summary?.revenue_hard || 0))} · {pct(summary?.share_hard)}
                  </div>
                </div>
                <div className="rounded-md border border-border/60 p-3">
                  <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
                    Blisko w czasie
                    <InfoTip tipId="content_purchase_proximate" />
                  </div>
                  <div className="mt-1 text-lg font-medium tabular-nums">{summary?.proximate ?? 0}</div>
                  <div className="text-muted-foreground">
                    {formatPLN(Number(summary?.revenue_proximate || 0))} · {pct(summary?.share_proximate)}
                  </div>
                </div>
                <div className="rounded-md border border-border/60 p-3">
                  <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground">
                    Bez powiązania
                    <InfoTip tipId="content_purchase_unlinked" />
                  </div>
                  <div className="mt-1 text-lg font-medium tabular-nums">{summary?.unlinked ?? 0}</div>
                  <div className="text-muted-foreground">
                    {formatPLN(Number(summary?.revenue_unlinked || 0))} · {pct(summary?.share_unlinked)}
                  </div>
                </div>
              </div>
              {(data?.notes_pl || []).map((n) => (
                <p key={n} className="text-muted-foreground">
                  {n}
                </p>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      {!q.isLoading && !q.isError ? (
        <>
          <TierList
            title="Powiązane (twarde)"
            tipId="content_purchase_hard"
            hint="Zakup z via mapowanym na karuzelę, YT albo oznaczone źródło kanału."
            rows={data?.hard ?? []}
            empty="Brak twardych powiązań via w tym oknie."
          />
          <TierList
            title="Blisko w czasie (korelacja)"
            tipId="content_purchase_proximate"
            hint="To nie atrybucja. Publikacja lub trigery w okolicy zakupu."
            rows={data?.proximate ?? []}
            empty="Brak zakupów z sąsiedztwem czasowym publikacji."
          />
          <TierList
            title="Bez powiązania"
            tipId="content_purchase_unlinked"
            hint="Paid EasyCart bez via treści i bez publikacji w oknie."
            rows={data?.unlinked ?? []}
            empty="Wszystkie zamówienia mają jakieś powiązanie."
          />
        </>
      ) : null}
    </div>
  );
}
